import { parse } from 'querystring';
import cookie from 'cookie';
import { readLocalStorage, writeLocalStorage } from 'redux/saga/helpers/local-storage';
import { getUserExperimentVariationMap } from 'helpers/optimizely';
import { isEmpty } from 'helpers/verify';

const UTM_VARIABLES = ['source', 'medium', 'campaign', 'content', 'term'];

const COOKIE_NAME = 'cpUtmValues';

const THIRTY_DAYS_MS = 1e3 * 60 * 60 * 24 * 30;

const stripQuerystring = location => location.replace(/\?.*$/, '');

const inviteUrl = location => {
  const isInviteUrl = /\/invite\/.*$/.test(location);

  // if this is an invite URL, this is our new value for `invite_url`, overwrite the old.
  if (isInviteUrl) {
    return stripQuerystring(location);
  }

  // if not, check if there's one in the cookie
  const cookies = cookie.parse(document.cookie);
  if (cookies && cookies[COOKIE_NAME]) {
    try {
      return JSON.parse(cookies[COOKIE_NAME]).invite_url;
    } catch (e) {
      /* noop */
    }
  }
  return null;
};

const parseUtmValues = (location = document.location.href) => {
  const queryParams = parse(
    location
      .split('?')
      .slice(1)
      .join('?')
  );

  const utmValues = {
    initial_url: stripQuerystring(location),
  };

  const inviteUrlOrNull = inviteUrl(location);
  if (inviteUrlOrNull) {
    utmValues.invite_url = inviteUrlOrNull;
  }

  UTM_VARIABLES.forEach(param => {
    if (queryParams[`utm_${param}`]) {
      let value = queryParams[`utm_${param}`];
      if (Array.isArray(value)) {
        value = value[value.length - 1];
      }
      utmValues[param] = value;
    }
  });

  return utmValues;
};

const getForApi = (localStorageKey = null) => {
  const cookies = localStorageKey ? {} : cookie.parse(document.cookie);
  const localStorageValue = localStorageKey ? readLocalStorage(localStorageKey) : null;
  const cohortMap = getUserExperimentVariationMap();

  const hasCohortMap = !isEmpty(cohortMap);
  const hasUtmValue = localStorageKey ? !!localStorageValue : cookies && cookies[COOKIE_NAME];

  if (!hasUtmValue && !hasCohortMap) {
    return undefined;
  }

  const values = {};

  if (hasUtmValue) {
    const cpUtmValues = (() => {
      try {
        return localStorageKey ? localStorageValue : JSON.parse(cookies[COOKIE_NAME]);
      } catch (err) {
        /* noop */
      }
    })();

    if (cpUtmValues) {
      values.initial_url = cpUtmValues.initial_url || '/';
      values.utm = UTM_VARIABLES.reduce((memo, param) => {
        const value = cpUtmValues[param];
        return Object.assign(memo, {
          [`utm_${param}`]: Array.isArray(value) ? value.pop() : value,
        });
      }, {});

      if (cpUtmValues.invite_url) {
        values.invite_url = cpUtmValues.invite_url;
      }
    }
  }

  if (hasCohortMap) {
    values.cohort = JSON.stringify(cohortMap);
  }

  return Object.keys(values).length ? values : undefined;
};

const save = (location, localStorageKey = null) => {
  const utmValues = parseUtmValues(location);
  const savedValue = localStorageKey ? readLocalStorage(localStorageKey) : cookie.parse(document.cookie)[COOKIE_NAME];

  if (!isEmpty(utmValues) || !savedValue) {
    if (localStorageKey) {
      writeLocalStorage(localStorageKey, utmValues);
    } else {
      document.cookie = cookie.serialize(COOKIE_NAME, JSON.stringify(utmValues), {
        expires: new Date(Date.now() + THIRTY_DAYS_MS),
        path: '/',
      });
    }
  }
};

export { getForApi };
export { parseUtmValues };
export { save };
export { COOKIE_NAME };
